import {
  BankOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  SearchOutlined,
  TagsOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CompanyDetails from '../components/CompanyDetails';
import CompanyForm from '../components/CompanyForm';
import api from '../config/api';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

interface EventOption {
  id: string;
  title: string;
  isActive?: boolean;
}

interface Company {
  id: string;
  name: string;
  taxNumber?: string;
  address?: string;
  contactEmail: string;
  contactPhone?: string;
  contactPerson?: string;
  billingAddress?: string;
  notes?: string;
  organizationId: string;
  organization?: {
    id: string;
    name: string;
  };
  _count?: {
    users: number;
    tickets: number;
  };
  createdAt: string;
  updatedAt: string;
}

const Companies: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [viewingCompany, setViewingCompany] = useState<Company | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterEventId, setFilterEventId] = useState<string | undefined>(undefined);
  const [withoutTicketsOnly, setWithoutTicketsOnly] = useState(false);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    totalTickets: 0,
  });
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEventId, withoutTicketsOnly]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      const all: EventOption[] = response.data?.data || [];
      // Csak aktív események jelenjenek meg a szűrőben
      setEvents(all.filter((e) => e.isActive !== false));
    } catch (error) {
      // Csendesen elnyeli — a szűrő üres marad
    }
  };

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterEventId) {
        params.eventId = filterEventId;
      }
      if (withoutTicketsOnly) {
        params.withoutTicketsOnly = 'true';
      }

      const response = await api.get('/companies', { params });
      setCompanies(response.data);

      const stats = response.data.reduce(
        (acc: any, company: Company) => {
          acc.totalCompanies++;
          acc.totalUsers += company._count?.users || 0;
          acc.totalTickets += company._count?.tickets || 0;
          return acc;
        },
        { totalCompanies: 0, totalUsers: 0, totalTickets: 0 }
      );

      setStats(stats);
    } catch (error) {
      message.error(t('companies.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setSearchText('');
    setFilterEventId(undefined);
    setWithoutTicketsOnly(false);
  };

  const handleCreate = () => {
    setEditingCompany(null);
    setIsModalVisible(true);
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setIsModalVisible(true);
  };

  const handleView = (company: Company) => {
    setViewingCompany(company);
    setIsDetailsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/companies/${id}`);
      message.success(t('companies.deleted'));
      fetchCompanies();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('companies.deleteFailed'));
    }
  };

  const handleFormSubmit = async () => {
    setIsModalVisible(false);
    fetchCompanies();
  };

  const filteredCompanies = companies.filter((company) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      company.name.toLowerCase().includes(q) ||
      company.contactEmail.toLowerCase().includes(q) ||
      (company.contactPerson?.toLowerCase().includes(q) ?? false) ||
      (company.taxNumber?.toLowerCase().includes(q) ?? false) ||
      (company.contactPhone?.toLowerCase().includes(q) ?? false) ||
      (company.address?.toLowerCase().includes(q) ?? false)
    );
  });

  const columns: ColumnsType<Company> = [
    {
      title: t('companies.columnName'),
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Space>
          <BankOutlined />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: t('companies.columnTaxNumber'),
      dataIndex: 'taxNumber',
      key: 'taxNumber',
      width: '15%',
      render: (text) => text || <Tag>{t('companies.na')}</Tag>,
    },
    {
      title: t('companies.columnContact'),
      key: 'contact',
      width: '35%',
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ gap: '2px' }}>
          {record.contactPerson && (
            <span style={{ fontSize: '13px', lineHeight: '1.2' }}>
              <TeamOutlined /> {record.contactPerson}
            </span>
          )}
          <Tooltip title={record.contactEmail}>
            <span style={{ fontSize: '13px', lineHeight: '1.2' }}>
              <MailOutlined /> {record.contactEmail.substring(0, 25)}
              {record.contactEmail.length > 25 && '...'}
            </span>
          </Tooltip>
          {record.contactPhone && (
            <span style={{ fontSize: '13px', lineHeight: '1.2' }}>
              <PhoneOutlined /> {record.contactPhone}
            </span>
          )}
          {record.address && (
            <span style={{ fontSize: '12px', color: '#666', lineHeight: '1.2' }}>
              📍 {record.address}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: t('companies.columnUsers'),
      key: 'users',
      width: '8%',
      sorter: (a, b) => (a._count?.users || 0) - (b._count?.users || 0),
      render: (_, record) => (
        <Tag icon={<TeamOutlined />} color="blue">
          {record._count?.users || 0}
        </Tag>
      ),
    },
    {
      title: t('companies.columnTickets'),
      key: 'tickets',
      width: '8%',
      sorter: (a, b) => (a._count?.tickets || 0) - (b._count?.tickets || 0),
      render: (_, record) => (
        <Tag icon={<TagsOutlined />} color="green">
          {record._count?.tickets || 0}
        </Tag>
      ),
    },
    {
      title: t('companies.columnActions'),
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title={t('companies.tooltipViewDetails')}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title={t('companies.tooltipEdit')}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('companies.deleteConfirmTitle')}
            description={t('companies.deleteConfirmDesc', { name: record.name })}
            onConfirm={() => handleDelete(record.id)}
            okText={t('companies.yes')}
            cancelText={t('companies.no')}
            okButtonProps={{ danger: true }}
          >
            <Tooltip title={t('companies.tooltipDelete')}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <BankOutlined /> {t('companies.pageTitle')}
      </Title>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('companies.statTotalCompanies')}
              value={stats.totalCompanies}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('companies.statTotalUsers')}
              value={stats.totalUsers}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('companies.statTotalTickets')}
              value={stats.totalTickets}
              prefix={<TagsOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Search
              placeholder={t('companies.searchPlaceholder')}
              allowClear
              enterButton={<SearchOutlined />}
              value={searchText}
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} md={5}>
            <Select
              placeholder={t('companies.filterEventPlaceholder')}
              allowClear
              value={filterEventId}
              onChange={setFilterEventId}
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} md={5}>
            <Space>
              <Switch checked={withoutTicketsOnly} onChange={setWithoutTicketsOnly} />
              <span>{t('companies.withoutTicketsOnly')}</span>
            </Space>
          </Col>
          <Col xs={12} md={3}>
            <Button onClick={handleResetFilters} style={{ width: '100%' }}>
              {t('companies.resetFilters')}
            </Button>
          </Col>
          <Col xs={12} md={3} style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('companies.newCompany')}
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredCompanies}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t('companies.paginationTotal', { count: total }),
          }}
        />
      </Card>

      {/* Company Form Modal */}
      <Modal
        title={editingCompany ? t('companies.modalEditTitle') : t('companies.modalNewTitle')}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <CompanyForm
          company={editingCompany}
          onSuccess={handleFormSubmit}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>

      {/* Company Details Modal */}
      <Modal
        title={t('companies.detailsTitle', { name: viewingCompany?.name ?? '' })}
        open={isDetailsModalVisible}
        onCancel={() => setIsDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsDetailsModalVisible(false)}>
            {t('companies.close')}
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setIsDetailsModalVisible(false);
              handleEdit(viewingCompany!);
            }}
          >
            {t('companies.editCompany')}
          </Button>,
        ]}
        width={1200}
      >
        {viewingCompany && <CompanyDetails companyId={viewingCompany.id} />}
      </Modal>
    </div>
  );
};

export default Companies;
